import { sapSyncSpecificationFileApi } from '@/api'
import { MappingType, SyncConfigDTO } from '@coac-gmbh/sap-sync-configuration-api'
import { addToast } from '@/composables/useToasts'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import i18n from '@/plugins/i18n'
import * as XLSX from 'xlsx'

export interface Characteristic {
  CharacterKeySource: string
  CharacterNameSource: string
  CharacterKeyDestination: string
  CharacterNameDestination: string
}

export interface Phrase {
  PhraseLibSource: string
  CharKeySource: string
  PhraseKeySource: string
  PhraseTextSource: string
  PhraseLibDestination: string
  PhraseKeyDestination: string
  PhraseTextDestination: string
  CharKeyDestination: string
}

export interface Property {
  sub_char_category_source: string
  identifier_source: string
  sub_char_category_destination: string
  identifier_destination: string
}

export interface Substance {
  [key: string]: string
}

export interface ItemData extends SyncConfigDTO {
  fileDataError?: boolean
  filesProcessed: FileData[]
}

export interface FileData {
  fileId?: string
  file?: File
  mappedType?: MappingType
  rows: RowData[]
  headers: string[]
}

export interface CreationForm {
  name: string
  sourceSystem: string
  destinationSystem: string
  federationConfig: boolean
}

export interface UpdateForm {
  name: string
}

export type RowData = Property | Phrase | Substance

export const CHARACTERISTIC_HEADERS = [
  'CharacterKeySource',
  'CharacterNameSource',
  'CharacterKeyDestination',
  'CharacterNameDestination',
]

export const PHRASE_HEADERS = [
  'PhraseLibSource',
  'PhraseKeySource',
  'PhraseTextSource',
  'CharKeySource',
  'PhraseLibDestination',
  'PhraseKeyDestination',
  'PhraseTextDestination',
  'CharKeyDestination',
]

export const PROPERTY_HEADERS = [
  'sub_char_category_source',
  'identifier_source',
  'sub_char_category_destination',
  'identifier_destination',
]

export const SUBSTANCE_HEADERS = window.ENV.SYNC_TOOL_HEADING_MAPPINGS_OF_SUBSTANCE_TYPE
  ? window.ENV.SYNC_TOOL_HEADING_MAPPINGS_OF_SUBSTANCE_TYPE?.split(',').map((s) => s.trim())
  : ['SubstanceSource', 'RecordNoSource', 'SubstanceDestination', 'RecordNoDestination']

export const AUTH_GROUP_HEADERS = ['SUBCAT_Source', 'AUTHGRP_Source', 'SUBCAT_Destination', 'AUTHGRP_Destination']

export const COMP_TYPE_HEADERS = ['CompType_Source', 'CompType_Destination']

export const IDENT_HEADER_HEADERS = [
  'Identifier_Source',
  'Spec_Type_Source',
  'Identifier_Destination',
  'Spec_Type_Destination',
]

export const INHERITANCE_HEADERS = ['InheritanceMappingSource', 'InheritanceMappingDestination']

export const USAGE_HEADERS = ['Rating_Source', 'ValidityArea_Source', 'Rating_Destination', 'ValidityArea_Destination']

export const LITERATURE_SOURCE_HEADERS = ['LiterarureSource_Source', 'LiteratureSource_Destination']

export interface MappingTypeConfig {
  mappingType: MappingType
  headers: string[]
  buttonTitle: string
  toastMessage?: string
  documentTemplateName: string
}

export const MAPPING_TYPE_CONFIGS: Record<MappingType, MappingTypeConfig> = {
  [MappingType.Property]: {
    mappingType: MappingType.Property,
    headers: PROPERTY_HEADERS,
    buttonTitle: i18n.global.t('Property configuration'),
    toastMessage: i18n.global.t('Property data found'),
    documentTemplateName: i18n.global.t('Property configuration template'),
  },
  [MappingType.Phrase]: {
    mappingType: MappingType.Phrase,
    headers: PHRASE_HEADERS,
    buttonTitle: i18n.global.t('Phrases configuration'),
    toastMessage: i18n.global.t('Phrase data found'),
    documentTemplateName: i18n.global.t('Phrases configuration template'),
  },
  [MappingType.Substance]: {
    mappingType: MappingType.Substance,
    headers: SUBSTANCE_HEADERS,
    buttonTitle: i18n.global.t('Substance configuration'),
    toastMessage: i18n.global.t('Substance data found'),
    documentTemplateName: i18n.global.t('Substance configuration template'),
  },
  [MappingType.Authgroup]: {
    mappingType: MappingType.Authgroup,
    headers: AUTH_GROUP_HEADERS,
    buttonTitle: i18n.global.t('Auth group configuration'),
    toastMessage: i18n.global.t('Auth group data found'),
    documentTemplateName: i18n.global.t('Auth group configuration template'),
  },
  [MappingType.Comptype]: {
    mappingType: MappingType.Comptype,
    headers: COMP_TYPE_HEADERS,
    buttonTitle: i18n.global.t('Component type configuration'),
    toastMessage: i18n.global.t('Component type data found'),
    documentTemplateName: i18n.global.t('Component type configuration template'),
  },
  [MappingType.Identheader]: {
    mappingType: MappingType.Identheader,
    headers: IDENT_HEADER_HEADERS,
    buttonTitle: i18n.global.t('Identifier configuration'),
    toastMessage: i18n.global.t('Identifier data found'),
    documentTemplateName: i18n.global.t('Identifier configuration template'),
  },
  [MappingType.Inheritance]: {
    mappingType: MappingType.Inheritance,
    headers: INHERITANCE_HEADERS,
    buttonTitle: i18n.global.t('Inheritance configuration'),
    toastMessage: i18n.global.t('Inheritance data found'),
    documentTemplateName: i18n.global.t('Inheritance configuration template'),
  },
  [MappingType.Usage]: {
    mappingType: MappingType.Usage,
    headers: USAGE_HEADERS,
    buttonTitle: i18n.global.t('Usage configuration'),
    toastMessage: i18n.global.t('Usage data found'),
    documentTemplateName: i18n.global.t('Usage configuration template'),
  },
  [MappingType.Literaturesource]: {
    mappingType: MappingType.Literaturesource,
    headers: LITERATURE_SOURCE_HEADERS,
    buttonTitle: i18n.global.t('Literature source configuration'),
    toastMessage: i18n.global.t('Literature source data found'),
    documentTemplateName: i18n.global.t('Literature source configuration template'),
  },
}

export function isSyncNotification(data: any): boolean {
  if (!data) return false

  const { source, destination, propHeaders, propData } = data
  if (!source || !destination || !propHeaders || !propData) return false

  return (
    typeof source === 'string' &&
    typeof destination === 'string' &&
    Array.isArray(propHeaders) &&
    Array.isArray(propData)
  )
}

export function getRowsDataFromFile(file: ArrayBuffer | string) {
  const data = new Uint8Array(file as ArrayBuffer)
  const workbook = XLSX.read(data, { type: 'array' })

  const sheetName = workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]

  const jsonData = XLSX.utils.sheet_to_json<RowData>(sheet, {
    defval: null,
  })
  return jsonData
}

export function generateFileFromRows(rowsData: RowData[], headersOrder: string[], file: File): File {
  const newSheet = XLSX.utils.json_to_sheet(rowsData, { header: headersOrder })

  const newWorkbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(newWorkbook, newSheet, 'Sheet1')

  const wbout = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([wbout])

  return new File([blob], file.name, { type: file.type })
}

export default function useSyncConfiguration() {
  const { t } = useI18n()
  const isSyncActionLoading = ref<boolean>(false)
  const sourceBusinessUnitOptions = ref<{ name: string; value: string }[]>([])
  const destinationBusinessUnitOptions = ref<{ name: string; value: string }[]>([])

  async function loadBusinessUnitOptions() {
    try {
      const { data } = await sapSyncSpecificationFileApi.getODataSystems()
      sourceBusinessUnitOptions.value = (data.sourceSystems ?? []).map((i) => ({ name: i, value: i }))
      destinationBusinessUnitOptions.value = (data.destinationSystems ?? []).map((i) => ({ name: i, value: i }))
    } catch (e) {
      addToast({ text: t('Error loading business Units options'), variant: 'danger' })
    }
  }

  loadBusinessUnitOptions()

  async function addSyncItemIntoSkipList(data: any, hideModal: () => void) {
    if (!isSyncNotification(data)) {
      addToast({ text: t('Invalid sync notification data'), variant: 'danger' })
      return
    }
    isSyncActionLoading.value = true
    const { source, destination, propHeaders, propData } = data

    try {
      await sapSyncSpecificationFileApi.addSkipList({
        skipListRequest: {
          sourceSystem: source,
          destinationSystem: destination,
          propHeader: propHeaders || [],
          propData: propData || [],
        },
      })
      addToast({ text: t('Item added to skip list'), variant: 'success' })
      hideModal()
    } catch (error) {
      addToast({ text: t('Error adding item to skip list'), variant: 'danger' })
    }

    setTimeout(() => {
      isSyncActionLoading.value = false
    }, 1000)
  }

  function getSyncSkipListModalFields(data: any) {
    return [
      { title: t('Config ID:'), value: data.data.configId || '---' },
      { title: t('Config Name:'), value: data.data.configName || '---' },
      { title: t('Source:'), value: data.data.source || '---' },
      { title: t('Destination:'), value: data.data.destination || '---' },
      { title: t('Headers:'), value: data.data.propHeaders?.join(', ') || t('No headers available') },
      { title: t('Data:'), value: data.data.propData?.join(', ') || t('No data available') },
    ]
  }

  return {
    sourceBusinessUnitOptions,
    destinationBusinessUnitOptions,
    isSyncActionLoading,
    addSyncItemIntoSkipList,
    getSyncSkipListModalFields,
  }
}
